/** Options Income & Hedging tab.
 *
 * Mock and live share one composition: strategy-family panels, dense
 * StrategyTable rows, and a sticky selected-strategy rail. Live binds
 * ranked_strategies[] workflow identity plus a per-row quote state. It never
 * binds mock premiums, weekly yield, legs, strikes, width or basket action,
 * and historical metrics live in the rail only.
 */
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CachedBadge, DataNotice, EmptyNote, KeyRow, SectionTitle, WorkspacePanel, useWorkspaceAdmin,
} from "@/workspace/primitives";
import { DteTimeline } from "@/workspace/DteTimeline";
import { DtePlaybookContext } from "@/workspace/DtePlaybook";
import { RegimePremiumStrip } from "@/workspace/RegimePremiumStrip";
import { TechnicalEntryStrip } from "@/workspace/TechnicalEntryContext";
import { StrategyEvidencePanel } from "@/workspace/StrategyEvidencePanel";
import { earningsConflict, strategyFamilies } from "@/workspace/mockTickerData";
import { LIVE_UNAVAILABLE } from "@/workspace/live/workspaceLiveUnavailable";
import { ROW_QUOTE_STATE, formatHistoricalStrategyLabel } from "@/workspace/live/workspaceLiveAdapter";
import { useWorkspaceSelection } from "@/workspace/selection";
import {
  applyResearchFocusSearch,
  parseHandoffSearch,
} from "@/workspace/handoff";

const SAFETY_RULES = [
  "Spread premiums are built from per-leg observed bid and ask fields. A mixed-leg midpoint is never synthesised across legs that do not each carry a usable bid and ask.",
  "Basket action is available only when every required quote field and a quote timestamp are present.",
  "Last-price and modelled figures are labelled as indicative estimates and do not change the product-action layer.",
];

const LIVE_SAFETY_RULES = [
  "Historical Backtest context is not a current option quote.",
  "Unverified option quote time is not treated as current, delayed, observed, executable, or valid premium pricing.",
  "Current product action requires valid broker-entitled current quote data.",
];

const BROKER_BANNER = "Connect a supported broker to view current option pricing.";

/** Quote-dependent cell copy. Only `delayed` and `realtime` may print numbers,
 * and no current state resolves to those today. */
const QUOTE_CELL = {
  no_broker: {
    legs: "Awaiting broker data",
    premium: "Locked",
    weekly: "Locked",
    weeklyLabel: "Awaiting broker data",
    state: "Connect a supported broker",
    tier: "blocking",
    mark: "!",
  },
  broker_no_quote: {
    legs: "Unavailable",
    premium: "Unavailable",
    weekly: "Unavailable",
    weeklyLabel: "Current quote unavailable",
    state: "Current quote unavailable",
    tier: "blocking",
    mark: "!",
  },
  quote_unverified: {
    legs: "Unavailable",
    premium: "Unavailable",
    weekly: "Unavailable",
    weeklyLabel: "Quote time unverified",
    state: LIVE_UNAVAILABLE.railQuoteSummary,
    tier: "blocking",
    mark: "!",
  },
  historical_only: {
    legs: "Not applicable",
    premium: "Not applicable",
    weekly: "Not applicable",
    weeklyLabel: "Historical research only",
    state: "Historical research only",
    tier: "info",
    mark: "○",
  },
  data_unavailable: {
    legs: "Unavailable",
    premium: "Unavailable",
    weekly: "Unavailable",
    weeklyLabel: "Data unavailable",
    state: "Data unavailable",
    tier: "blocking",
    mark: "!",
  },
};

function gatedCells(quoteState) {
  if (!quoteState) return null;
  if (quoteState === ROW_QUOTE_STATE.delayed || quoteState === ROW_QUOTE_STATE.realtime) return null;
  return QUOTE_CELL[quoteState] || QUOTE_CELL.data_unavailable;
}

const PROTECTION_SHELF_IDS = ["protective_put", "collar"];

/** The live shelves are the mockup families. Only the row source differs:
 * shelf membership is display grouping, the URL family stays canonical. */
const LIVE_SHELF_SOURCE = {
  income: { families: ["premium_selling"] },
  directional: { families: [] },
  protection: { ids: PROTECTION_SHELF_IDS },
  volatility: { families: ["long_vol"] },
  repair: { families: [] },
};

const LIVE_SHELVES = strategyFamilies().map((f) => ({
  id: f.id,
  label: f.label,
  blurb: f.blurb,
  families: [],
  ...LIVE_SHELF_SOURCE[f.id],
}));

export function filterResearchChips(rows, shelfId) {
  const list = Array.isArray(rows) ? rows : [];
  if (!shelfId || shelfId === "all") return list;
  const shelf = LIVE_SHELVES.find((s) => s.id === shelfId);
  if (!shelf) return list.filter((row) => row.family === shelfId);
  if (shelf.ids) return list.filter((row) => shelf.ids.includes(row.strategy_id));
  return list.filter(
    (row) => shelf.families.includes(row.family) && !PROTECTION_SHELF_IDS.includes(row.strategy_id),
  );
}

/** Families present in the payload but not covered by a mockup shelf keep
 * their canonical label rather than being dropped or renamed. */
function extraShelves(rows) {
  const claimed = new Set();
  LIVE_SHELVES.forEach((shelf) => {
    filterResearchChips(rows, shelf.id).forEach((row) => claimed.add(row.strategy_id));
  });
  const leftovers = (rows || []).filter((row) => !claimed.has(row.strategy_id));
  const seen = [];
  leftovers.forEach((row) => {
    const key = row.family || "unclassified";
    if (!seen.some((s) => s.id === key)) {
      seen.push({
        id: key,
        label: row.familyDisplay || formatHistoricalStrategyLabel(key) || "Other historical research",
        blurb: "Additional strategies in the current historical dataset.",
        families: [row.family],
        extra: true,
      });
    }
  });
  return seen;
}

function slug(label) {
  return String(label || "").toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");
}

function chipStyle(active) {
  return {
    color: active ? "#3B82F6" : "var(--hoip-text-secondary)",
    borderColor: active ? "#3B82F6" : "var(--hoip-border)",
    background: active ? "rgba(59,130,246,0.08)" : "var(--hoip-card)",
  };
}

function StrategyTable({ rows, selectedKey, onSelect, tenor, live }) {
  if (!rows.length) {
    return (
      <EmptyNote>
        {live
          ? "No strategies in this family in the current historical dataset."
          : "No structures in this family for the selected expiry."}
      </EmptyNote>
    );
  }
  return (
    <div className="overflow-x-auto min-w-0">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left uppercase tracking-wide text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            <th className="py-1 pr-3 font-medium whitespace-nowrap">Structure</th>
            <th className="py-1 pr-3 font-medium whitespace-nowrap">Legs</th>
            <th className="py-1 pr-3 font-medium whitespace-nowrap">Expiry</th>
            <th className="py-1 pr-3 font-medium text-right whitespace-nowrap">Premium</th>
            <th className="py-1 pr-3 font-medium text-right whitespace-nowrap">Weekly</th>
            <th className="py-1 font-medium whitespace-nowrap">Data / event / action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const gated = gatedCells(r.quoteState);
            const label = r.strategy || r.displayName || formatHistoricalStrategyLabel(r.strategy_id);
            const key = r.strategy || r.strategy_id;
            const active = key === selectedKey;
            const layers = gated
              ? {
                data: { tier: gated.tier, mark: gated.mark, text: gated.state },
                event: tenor?.event || { tier: "info", mark: "●", text: "Earnings state unavailable" },
                action: { tier: "blocking", mark: "!", text: LIVE_UNAVAILABLE.currentActionUnavailable },
              }
              : r.layers;
            return (
              <tr
                key={r.id || r.strategy_id}
                className="border-t align-top cursor-pointer"
                style={{
                  borderColor: "var(--hoip-divider)",
                  background: active ? "rgba(59,130,246,0.08)" : "transparent",
                }}
                onClick={() => onSelect(r)}
                data-testid={`strategy-row-${slug(label)}`}
                data-selected={active ? "true" : "false"}
                data-quote-state={r.quoteState || "mock"}
              >
                <td className="py-1.5 pr-3 whitespace-nowrap">
                  <div className="whitespace-nowrap">{label}</div>
                  {/* Secondary label may wrap; the strategy name above never does. */}
                  <div className="text-[9px] whitespace-normal" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {gated ? (r.familyDisplay ? `Registry family: ${r.familyDisplay}` : "Historical research identity") : r.metric}
                  </div>
                </td>
                <td className="py-1.5 pr-3 font-mono-data text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>
                  {gated ? gated.legs : r.structure}
                </td>
                <td className="py-1.5 pr-3 font-mono-data text-[10px]">
                  {gated ? (tenor?.date || "Tenor unavailable") : r.expiry}
                  <span style={{ color: "var(--hoip-text-tertiary)" }}>
                    {gated
                      ? (tenor?.dte != null ? ` · ${tenor.dte}d requested tenor` : " · requested tenor")
                      : ` · ${r.dte}d`}
                  </span>
                </td>
                <td className="py-1.5 pr-3 font-mono-data text-right whitespace-nowrap" style={gated ? { color: "var(--hoip-text-tertiary)" } : undefined}>
                  {gated ? gated.premium : r.premium}
                </td>
                <td className="py-1.5 pr-3 text-right whitespace-nowrap" data-testid={`weekly-${slug(label)}`}>
                  <div className="font-mono-data" style={gated ? { color: "var(--hoip-text-tertiary)" } : undefined}>
                    {gated ? gated.weekly : (r.weekly?.value ?? "Unavailable")}
                  </div>
                  <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                    {gated ? gated.weeklyLabel : r.weekly?.label}
                  </div>
                </td>
                <td className="py-1.5 min-w-[14rem]">
                  <div className="flex flex-col gap-1" data-testid={`strategy-layers-${slug(label)}`}>
                    {[
                      ["A", layers?.data],
                      ["B", layers?.event],
                      ["C", layers?.action],
                    ].map(([k, layer]) => (
                      <div
                        key={k}
                        className="text-[10px] leading-snug"
                        style={{
                          color: layer?.tier === "blocking" ? "#F87171" : layer?.tier === "caution" ? "#D97706" : "var(--hoip-text-secondary)",
                        }}
                      >
                        <span aria-hidden="true">{layer?.mark || "●"} </span>
                        <span className="uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>{k} · </span>
                        {layer?.text}
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function OptionsIncomeHedgingTab({ t }) {
  const isAdmin = useWorkspaceAdmin();
  const [family, setFamily] = useState("all");
  const [params, setParams] = useSearchParams();
  const {
    selectedExpiry,
    setSelectedExpiry,
    selectedStrategy,
    setSelectedStrategy,
    selectedRow,
    rows: allRows,
    researchRows,
    selectedResearchStrategyId,
  } = useWorkspaceSelection();

  const liveExp = (t.availableExpirations || []).find((e) => e.date === selectedExpiry) || t.availableExpirations?.[0];
  const dte = t.live ? liveExp?.dte : (selectedRow?.dte || allRows[0]?.dte);
  const conflict = earningsConflict(t.earnings, dte);
  const stripFamily = family === "protection" ? "protection" : "income";

  const shelves = useMemo(() => {
    if (!t.live) {
      return strategyFamilies().map((f) => ({ id: f.id, label: f.label, blurb: f.blurb, strategies: f.strategies }));
    }
    return [...LIVE_SHELVES, ...extraShelves(researchRows)];
  }, [t.live, researchRows]);

  const shown = useMemo(
    () => (family === "all" ? shelves : shelves.filter((f) => f.id === family)),
    [family, shelves],
  );

  const tenor = useMemo(
    () => ({
      date: liveExp?.date || null,
      dte: liveExp?.dte ?? null,
      event: { tier: conflict.state === "inside" ? "caution" : "info", mark: conflict.state === "inside" ? "▲" : "●", text: conflict.label },
    }),
    [liveExp, conflict],
  );

  const selectResearch = (row) => {
    const parsed = parseHandoffSearch(params);
    const next = applyResearchFocusSearch(parsed, { strategyId: row.strategy_id, family: row.family }, {
      asOfDate: t.asOf?.ohlcv,
      expirations: t.availableExpirations,
    });
    setParams(next, { replace: true });
  };

  const onRowSelect = (row) => {
    if (t.live) selectResearch(row);
    else setSelectedStrategy(row.strategy);
  };

  return (
    <div className="space-y-3">
      <DteTimeline ticker={t} selectedExpiry={selectedExpiry} onSelectExpiry={setSelectedExpiry} />

      <RegimePremiumStrip ticker={t} dte={dte} familyId={t.live ? "income" : stripFamily} />

      {t.live ? null : (
        <TechnicalEntryStrip ticker={t} dte={dte} conflictLabel={conflict.label} />
      )}

      <DtePlaybookContext ticker={t} dte={dte} />

      <WorkspacePanel
        title="Strategy safety rules"
        hint={t.live
          ? "Historical research is not a current priced structure. Unverified quote time blocks current premium claims."
          : "Copy mirrors the production pricing rules. The mockup does not run validation; it shows how each state is presented."}
        testid="options-safety"
      >
        <ul className="space-y-1.5">
          {(t.live ? LIVE_SAFETY_RULES : SAFETY_RULES).map((r) => (
            <li key={r} className="flex gap-2 text-[11px] leading-snug" style={{ color: "var(--hoip-text-secondary)" }}>
              <span style={{ color: "var(--hoip-text-tertiary)" }}>·</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </WorkspacePanel>

      <div
        className={`flex gap-1.5 min-w-0 pb-0.5 ${t.live ? "overflow-x-auto" : "flex-wrap"}`}
        data-testid="options-family-filter"
      >
        <div className={`flex gap-1.5 min-w-0 ${t.live ? "" : "contents"}`} data-testid="research-family-pills">
          {[{ id: "all", label: "All families" }, ...shelves].map((f) => {
            const active = family === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFamily(f.id)}
                data-testid={t.live ? `research-shelf-${f.id}` : undefined}
                className="text-[10px] px-2 py-1 rounded-sm border whitespace-nowrap shrink-0"
                style={{
                  color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                  background: active ? "var(--hoip-text-primary)" : "var(--hoip-card)",
                  borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className={`grid grid-cols-1 gap-3 items-start ${t.live || selectedRow ? "xl:grid-cols-[minmax(0,1fr)_minmax(18rem,26rem)]" : ""}`}
        data-testid="options-research-grid"
      >
        <div className="space-y-3 min-w-0" data-testid="options-research-primary">
          {shown.map((f) => {
            const familyRows = t.live
              ? filterResearchChips(researchRows, f.id)
              : allRows.filter((r) => r.family === f.id);
            const chips = t.live
              ? familyRows.map((r) => ({ key: r.strategy_id, label: r.displayName, row: r }))
              : (f.strategies || []).map((s) => ({ key: s, label: s, row: null }));
            return (
              <WorkspacePanel
                key={f.id}
                title={f.label}
                subtitle={t.live
                  ? `${f.blurb} · historical research workflow`
                  : `${f.blurb} · ${selectedExpiry}`}
                hint={t.live
                  ? "Rows come from ranked_strategies[]. Quote-dependent cells stay locked until broker-entitled quote data is available."
                  : "Structures, strikes and premiums are fixtures. Selecting an expiry filters this table. No chain was read."}
                testid={`options-family-${f.id}`}
              >
                {chips.length ? (
                <div className="mb-2">
                  <SectionTitle>Covered in this family</SectionTitle>
                  <div
                    className={`flex gap-1.5 min-w-0 ${t.live ? "overflow-x-auto" : "flex-wrap"}`}
                    data-testid={t.live ? `research-chips-${f.id}` : undefined}
                  >
                    {chips.map((chip) => (
                      t.live ? (
                        <button
                          key={chip.key}
                          type="button"
                          data-testid={`research-chip-${chip.key}`}
                          onClick={() => selectResearch(chip.row)}
                          className="text-[9px] px-1.5 py-0.5 rounded-sm border font-mono-data whitespace-nowrap shrink-0"
                          style={chipStyle(selectedResearchStrategyId === chip.key)}
                        >
                          {chip.label}
                        </button>
                      ) : (
                        <span
                          key={chip.key}
                          className="text-[9px] px-1.5 py-0.5 rounded-sm border font-mono-data"
                          style={{ borderColor: "var(--hoip-border)", color: "var(--hoip-text-secondary)" }}
                        >
                          {chip.label}
                        </span>
                      )
                    ))}
                  </div>
                </div>
                ) : null}
                <StrategyTable
                  rows={familyRows}
                  selectedKey={t.live ? selectedResearchStrategyId : selectedStrategy}
                  onSelect={onRowSelect}
                  tenor={tenor}
                  live={t.live}
                />
                {t.live && familyRows.length ? (
                  <p
                    className="text-[10px] mt-2 pt-2 border-t leading-snug"
                    style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)" }}
                    data-testid={`options-broker-banner-${f.id}`}
                  >
                    {BROKER_BANNER}
                  </p>
                ) : null}
              </WorkspacePanel>
            );
          })}
        </div>

        {t.live ? (
          <div className="min-w-0 xl:sticky xl:top-3 self-start" data-testid="options-research-rail">
            <StrategyEvidencePanel
              ticker={t}
              researchFocus={t.researchFocus}
              currentQuote={t.currentQuote}
            />
          </div>
        ) : selectedRow ? (
          <div className="min-w-0 xl:sticky xl:top-3 self-start">
            <StrategyEvidencePanel row={selectedRow} ticker={t} />
          </div>
        ) : null}
      </div>

      {!t.live && !selectedRow ? <StrategyEvidencePanel row={null} ticker={t} /> : null}

      {t.live && isAdmin ? (
        <WorkspacePanel title="Quote-state diagnostics" notice={<CachedBadge />} testid="options-abc">
          <KeyRow label="Current quote availability">{t.currentQuote?.availability || "unverified_timestamp"}</KeyRow>
          <KeyRow label="Row quote state">{t.rowQuoteState || ROW_QUOTE_STATE.data_unavailable}</KeyRow>
          <KeyRow label="Pricing method">{LIVE_UNAVAILABLE.pricingMethodUnavailable}</KeyRow>
          <KeyRow label="Option quote as-of">{t.asOf?.optionQuote || "timestamp unavailable"}</KeyRow>
          <KeyRow label="Fetched">{t.asOf?.optionFetched || t.fetchedAt || "timestamp unavailable"}</KeyRow>
        </WorkspacePanel>
      ) : null}

      <WorkspacePanel title="How to read these figures" testid="options-footnote">
        <DataNotice
          adminText={t.live
            ? "This preview maps existing cached GET fields only. recommended_strategy is unused. No refresh, evaluate, payoff or write calls."
            : "Production pricing resolves per-leg via premium_for_pricing_basis and gates selection on option_freshness plus wing_scenarios.validation_errors. Historical fields map to existing backtest outputs (win_rate, avg_return_on_risk, sample_size, profit_factor, max_loss) and regime_fit. This mockup renders presentation states only."}
          plainText={t.live
            ? "Historical Backtest context is not a current option quote. Current priced structures are unavailable without a verified option quote time."
            : "Data may be delayed. Figures shown as estimates are indicative and are not order prices."}
        />
        {!t.live && !isAdmin ? (
          <p className="text-[10px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>
            Indicative estimates and unavailable quote fields do not change the product-action layer by themselves unless required data is missing.
          </p>
        ) : null}
      </WorkspacePanel>
    </div>
  );
}
